import { Link } from "react-router-dom";

export default function FhaVsConventionalLoan() {
    return (
        <>
            <p>
                When you're buying a home and don't have 20% to put down, the two most common paths are FHA loans and
                conventional loans with private mortgage insurance. Plenty of buyers default to FHA because they've heard it's
                "easier to qualify for." Sometimes that's the right call. But FHA isn't always cheaper, and conventional loans
                have gotten more accessible. Here's how to figure out which one actually saves you money.
            </p>

            <h2>The Basic Differences</h2>
            <p>
                <strong>FHA loans</strong> are backed by the Federal Housing Administration. Because the government insures them,
                lenders take on less risk and are willing to approve borrowers with lower credit scores and smaller down payments.
                Minimum down payment is 3.5% with a 580+ credit score; 10% with a score between 500 and 579.
            </p>
            <p>
                <strong>Conventional loans</strong> aren't government-backed. Lenders set their own standards (within
                Fannie Mae/Freddie Mac guidelines). You can put as little as 3% down with some programs (like Fannie's
                HomeReady or Freddie's Home Possible), though 5% is more common. Credit score minimums are typically 620–640,
                with the best rates reserved for 740+.
            </p>

            <h2>Mortgage Insurance: The Key Difference</h2>
            <p>
                Both loan types require mortgage insurance when you put less than 20% down — but they work very differently, and
                this is where FHA loans can become significantly more expensive.
            </p>
            <p>
                <strong>FHA mortgage insurance (MIP):</strong> You pay an upfront premium of 1.75% of the loan amount (can be
                rolled into the loan), plus an annual premium of 0.55% for most 30-year loans. And here's the painful part:
                unless you put at least 10% down, the annual MIP stays for the life of the loan. It never goes away. The only
                way to get rid of it is to refinance into a conventional loan.
            </p>
            <p>
                <strong>Conventional PMI:</strong> Typically 0.2%–2.0% annually depending on your credit score and LTV.
                Once you reach 20% equity (either through payments or appreciation), you can request cancellation. At 22% equity
                via scheduled payments, lenders are required by law to cancel it automatically.
            </p>
            <p>
                On a $300,000 loan: FHA MIP runs about $137/month and never drops. Conventional PMI at 0.8% runs about
                $200/month initially, but drops to $0 once you hit 20% equity — potentially saving you tens of thousands over
                the life of the loan.
            </p>
            <p>
                Use the <Link to="/calculator/fha-loan">FHA loan calculator</Link> to see total costs including MIP for your
                specific loan amount and scenario.
            </p>

            <h2>Credit Score Scenarios: When Each Makes Sense</h2>
            <p>
                <strong>Score below 620:</strong> You likely need FHA. Conventional lending becomes very difficult or
                impossible below that threshold.
            </p>
            <p>
                <strong>Score 620–679:</strong> FHA may offer better rates, but run the numbers. The lower FHA rate is sometimes
                offset by the lifetime MIP. Compare total cost of ownership, not just the monthly payment.
            </p>
            <p>
                <strong>Score 680–719:</strong> This is the gray zone. Conventional PMI is competitive here, and you gain the
                ability to cancel it. FHA MIP is permanent unless you refinance.
            </p>
            <p>
                <strong>Score 720+:</strong> Conventional almost always wins. You'll get competitive rates without lifetime
                mortgage insurance.
            </p>

            <h2>Loan Limits</h2>
            <p>
                FHA loan limits in 2026 are $524,225 for a single-family home in most areas, with higher limits in high-cost
                markets (up to $1,209,750 in places like San Francisco and New York). Conventional conforming loan limits are
                $806,500 in most areas. If you're buying above these thresholds, you're looking at jumbo loans, which have their
                own requirements.
            </p>

            <h2>Down Payment Comparison</h2>
            <p>
                On a $350,000 home:
            </p>
            <p>
                FHA minimum down: $12,250 (3.5%) + upfront MIP of $5,906 = $18,156 effectively in at close<br />
                Conventional minimum (3%): $10,500 with no upfront MIP
            </p>
            <p>
                FHA's upfront MIP (typically rolled into the loan) means your actual loan balance is higher than the purchase
                price minus down payment. On a $350,000 purchase with 3.5% down, your FHA loan balance starts at roughly
                $343,561 after rolling in MIP — not $337,750.
            </p>

            <h2>Property Standards</h2>
            <p>
                FHA has stricter property condition requirements. Appraisers check for health and safety issues — things like
                peeling paint, missing handrails, or roof problems that a conventional appraisal might note but not require
                repair before closing. If you're buying a fixer-upper, conventional financing is usually easier.
            </p>

            <h2>Which One Actually Saves You More?</h2>
            <p>
                Run this analysis:
            </p>
            <p>
                1. Get quotes for both loan types from real lenders (not rate teaser websites). Ask for Loan Estimates, not just
                rate quotes.<br />
                2. Compare total monthly payment including MIP/PMI.<br />
                3. Calculate when conventional PMI drops off and what total paid looks like at 5, 10, and 30 years.<br />
                4. Factor in the FHA upfront MIP if you're rolling it into the loan — it increases your balance and the interest
                you pay on it.
            </p>
            <p>
                For buyers with solid credit who plan to stay in the home long-term, conventional with PMI usually wins because
                the mortgage insurance eventually goes away. For buyers with lower credit or limited savings, FHA can be the only
                viable path — just go in knowing the permanent MIP cost and plan to refinance if your equity and credit improve.
            </p>

            <h2>The Bottom Line</h2>
            <p>
                FHA isn't always cheaper and conventional isn't always harder to get. The right answer depends on your credit
                score, down payment, and how long you plan to keep the loan. Plug your numbers into the{" "}
                <Link to="/calculator/fha-loan">FHA loan calculator</Link> and compare them against a conventional quote before
                making a decision.
            </p>
        </>
    );
}